import { useRoute, provide, ssrRef, ref, onMounted, useContext, watch, useMeta } from '@nuxtjs/composition-api';
import { useDevice } from '@/composables/useDevice';
import { getCategoryInfoByPath } from '@/modules/category/utils/categoryHelpers';
import { useMetadataConfig } from '../config/configUtils';
import { useAuth } from '../auth/state/useAuth';
import { adModes } from './constants/modes';
import { loadAdScript } from './scripts/adloader';
import { targetings } from './constants/targetings';
import { LANG, IS_PROD } from '~/constants/config';
/**
 * Disable ads for devs
 *
 * ?noads var ise sadece o sayfa icin reklamlar kaldırılır.
 * ?dev-noads var ise cookie setlenir. Tüm sayfalar için reklamlar kaldırılır.
 * @param {*} $cookies
 * @returns {boolean}
 */
export const isDevMode = (route, $cookies) => {
  // ?dev-noads var ise cookie setle
  if (route?.value?.query?.['dev-noads'] !== undefined) {
    $cookies.set('dev-noads', 1, {
      path: '/',
      maxAge: 60 * 60 * 24 * 30, // 30 gün
    });
    return true;
  }

  return $cookies.get('dev-noads') || route.value?.query?.noads !== undefined;
};
export const initializeAdLoaders = () => {
  const route = useRoute();
  const adConfig = ssrRef({});
  const pageType = ssrRef({});
  const sponsored = ssrRef({});
  // const context = useContext();
  const { isMobile } = useDevice();
  const {
    $service,
    app: { $cookies },
  } = useContext();
  const { user } = useAuth();

  useMeta(() => {
    const adTags = { script: [], __dangerouslyDisableSanitizersByTagID: {} };
    if (!adConfig.value?.developer && adConfig.value?.mode?.scripts) {
      adConfig.value.mode.scripts.forEach((script) => {
        loadAdScript(adTags, script, adConfig.value?.mode?.options?.[script], adConfig, isMobile, user);
      });
    }
    return adTags;
  });
  onMounted(() => {
    if (!adConfig.value?.developer && adConfig.value?.mode?.clientScripts) {
      useMeta(() => {
        const adTags = { script: [], __dangerouslyDisableSanitizersByTagID: {} };
        adConfig.value.mode.clientScripts.forEach((script) => {
          loadAdScript(adTags, script, adConfig.value?.mode?.options?.[script], adConfig, isMobile, user);
        });
        return adTags;
      });
    }
  });

  // Fake backend server response for STN
  const serversideDataGenerator = (data, adConfigResult = adConfig, type = pageType.value) => {
    const onlyMasthead = !!data?.value?.tags?.includes('~only-masthead');
    const decidedMode = ref('default');
    const resultTargetings = ref([]);
    const developerMode = isDevMode(route, $cookies);

    const generateArticleTargetings = (data) => {
      const tags = data.value?.tags;
      const categories = data.value?.categories;
      const breadcrumb = data.value?.breadcrumb;
      // Targetings Tags & Categories
      categories.forEach((cat) => {
        resultTargetings.value.push({
          targeting: {
            kategori: cat?._id,
          },
        });
      });
      breadcrumb.forEach((cat) => {
        const isTargeting = sponsored.value.find((val) => val.adConfig?.linkedCategories?.includes(cat?._id));
        if (isTargeting) {
          const item = {
            id: isTargeting._id,
            targeting: {
              etiket: isTargeting.adConfig?.targetings.value,
            },
            isSponsored: isTargeting.isSponsored,
          };
          resultTargetings.value.push(item);
          if (item?.isSponsored) {
            adConfigResult.value.sponsoredTag = { ...item, type: 'category' };
          }
        }
      });
      tags.forEach((tag) => {
        if (!tag.startsWith('~')) {
          resultTargetings.value.push({
            targeting: {
              etiket: tag,
            },
          });
        }
        const sponsoredTag = sponsored.value.find((value) => value.name === tag);
        if (sponsoredTag && sponsoredTag.isSponsored) {
          const data = {
            eventName: sponsoredTag.name,
            id: sponsoredTag._id,
            targeting: {
              etiket: sponsoredTag.adConfig?.targetings.value,
            },
            isSponsored: sponsoredTag.isSponsored,
          };
          if (data?.targeting) {
            resultTargetings.value.push(data);
          }
          if (data?.isSponsored) {
            adConfigResult.value.sponsoredTag = { ...data, type: 'tag' };
          }
        }
        // return sponsoredKeys?.includes(tag);
        return sponsored.value.some((val) => val.adConfig?.targetings.value === tag);
      });
    };

    if (type === 'Homepage') {
      resultTargetings.value.push({
        targeting: {
          kategori: 'homepage',
          // PageType: 'Homepage',
        },
      });
    } else if (type === 'Category') {
      resultTargetings.value.push({
        targeting: {
          kategori: data.value._id,
          // PageType: 'Category',
        },
      });
      if (data.value?.ancestors?.length) {
        data.value?.ancestors.forEach((cat) => {
          resultTargetings.value.push({
            targeting: {
              kategori: getCategoryInfoByPath(cat)?._id,
            },
          });
        });
      }
      const ancestorsNames = data.value?.ancestors?.map((ancestor) => getCategoryInfoByPath(ancestor).name).reverse();
      const ancestorsId = data.value?.ancestors?.map((ancestor) => getCategoryInfoByPath(ancestor)._id).reverse();
      ancestorsNames.unshift(data.value.name); // Add main category
      ancestorsId.unshift(data.value._id);
      const sponsoredCategory = sponsored.value.find((val) => val.adConfig?.linkedCategories?.some((v) => ancestorsId.includes(v)));
      if (sponsoredCategory) {
        decidedMode.value = 'sponsored';
        resultTargetings.value.push({
          id: sponsoredCategory._id,
          targeting: { etiket: sponsoredCategory.adConfig?.targetings.value },
          isSponsored: sponsoredCategory.isSponsored,
        });
      }
    } else if (type === 'Tag') {
      resultTargetings.value.push({
        targeting: {
          // PageType: 'Labels',
          etiket: data.value.name,
        },
      });
      const sponsoredTag = sponsored.value.find((val) => val.name === data.value.name);
      if (sponsoredTag && sponsoredTag.isSponsored) {
        decidedMode.value = 'sponsored';
        resultTargetings.value.push({
          id: sponsoredTag._id,
          targeting: { etiket: sponsoredTag.adConfig?.targetings.value },
          isSponsored: sponsoredTag.isSponsored,
        });
      }
    } else if (type === 'Article' || type === 'Webview') {
      // resultTargetings.value.push({
      //   targeting: {
      //     PageType: 'Article',
      //   },
      // });
      const creationYear = new Date(data.value.dates.updated).getFullYear();
      const showAds = data.value.flags.showAds;
      const tags = data.value?.tags;
      generateArticleTargetings(data);
      if (adConfigResult.value.sponsoredTag) {
        decidedMode.value = 'sponsored';
      } else if (tags.includes('Premium User')) {
        decidedMode.value = 'PremiumUser';
      } else if (
        (creationYear < 2017 || tags.includes('+18') || tags.includes('#cinsellik') || tags.includes('#seks')) &&
        type !== 'Webview'
      ) {
        decidedMode.value = 'onlyCriteo';
        adConfigResult.value.criteoArticleUnits = {
          placements: [],
        };
      } else if (showAds === false) {
        decidedMode.value = 'noAds';
      }
    } else {
      decidedMode.value = type;
      if (data?.value?.type === 'Vitrin') {
        const item = targetings?.[LANG]?.vitrin?.[data.value?.targetingKey];
        if (item?.targeting) {
          resultTargetings.value.push(item);
        }
      } else if (data?.value?.type === 'Redirect') {
        if (data?.value?.article) {
          generateArticleTargetings(ref(data.value.article));
        }
      }
      type = 'Custom';
    }

    // Reklam testlerinde kullanılmak üzere frontend:true hedeflemesi tanımlandı.
    !IS_PROD && resultTargetings.value.push({ targeting: { frontend: 'true' } });

    // New targeting system
    const targetingsStorage = {};
    if (resultTargetings.value.length > 0) {
      resultTargetings.value.forEach((item) => {
        Object.keys(item.targeting).forEach((key) => {
          targetingsStorage.hasOwnProperty(key)
            ? targetingsStorage[key]?.push(item.targeting[key])
            : (targetingsStorage[key] = [item.targeting[key]]);
        });
      });
    }
    const mode = adModes?.[LANG]?.[type]?.[decidedMode.value];
    adConfigResult.value.fetchData = data;
    adConfigResult.value.type = type;
    adConfigResult.value.mode = mode;
    adConfigResult.value.targetings = targetingsStorage;
    adConfigResult.value.developer = developerMode;
    adConfigResult.value.onlyMasthead = onlyMasthead;
    adConfigResult.value.adUnitCounter = {};
    adConfigResult.value.decidedMode = decidedMode;
    adConfigResult.value.recommendedArticles = [];
    adConfigResult.value.prebidUnits = [];
    adConfigResult.value.recommendedArticlesLoaded = ref(false);
    return adConfigResult;
  };

  const generateSponsoredArticles = async (adConfig) => {
    // Sponsored Articles Recommendation
    if ((adConfig.value.type === 'Article' || adConfig.value.type === 'Webview') && adConfig.value.decidedMode.value === 'sponsored') {
      try {
        const neededRandomArticles =
          document.querySelectorAll(`article[data-legacy-id='${adConfig.value.fetchData.value.legacyId}'] .article-oads`).length * 2;
        if (neededRandomArticles === 0) return;
        let data;
        if (adConfig.value?.sponsoredTag?.type === 'tag') {
          ({ data } = await $service.tag.getTagRandomFeed(adConfig.value?.sponsoredTag?.id, 1, neededRandomArticles));
        } else {
          ({ data } = await $service.category.getCategoryRandomFeed(adConfig.value?.sponsoredTag?.id, 1, neededRandomArticles));
        }
        const selectedArticles = [];
        adConfig.value.getSponsoredArticle = () => {
          const articleLength = data.length;
          let random = Math.floor(Math.random() * articleLength);
          if (selectedArticles.length !== articleLength) {
            while (selectedArticles.includes(random)) {
              random = Math.floor(Math.random() * articleLength);
            }
            selectedArticles.push(random);
          }
          return data[random];
        };
        watch(isMobile, () => {
          selectedArticles.length = 0;
        });
        adConfig.value.recommendedArticlesLoaded.value = true;
      } catch {
        // console.error('Cannot get recommended articles');
      }
    }
  };

  const generateAdConfig = (type) => {
    provide('adConfig', adConfig);
    pageType.value = type;
    const { sponsoredTags } = useMetadataConfig();
    sponsored.value = sponsoredTags;
    onMounted(async () => {
      // dev mode check
      if (adConfig.value?.developer) {
        return;
      }
      await generateSponsoredArticles(adConfig);
    });
    return serversideDataGenerator;
  };
  return { generateAdConfig, serversideDataGenerator, generateSponsoredArticles };
};
